/**
 * organization-brand — the brand a buyer sees: name, organization line, seed
 * color and the wordmark logo. Settings and the onboarding stepper each mount
 * it and each save it their own way, so it is controlled: the brand, the
 * logo preview and the picked file's appraisal come down; edits go up as
 * `brand-change`, `logo-pick` (a file of an accepted type and size),
 * `logo-remove`, `wallet-mark-change` and `wallet-mark-request`.
 *
 * The logo keeps brand-logo.view.ts's CSS mask-image rendering (one
 * monochrome, transparent asset adapts to both light and dark with no
 * second file), so the preview shows the picked file in both schemes before
 * anything is saved. Measuring a picked file for the wallet pass is the
 * host's: it is canvas work over the Blob, and the verdict comes back down
 * as `logoAppraisal`.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { $notify } from "@mhmo91/schmancy/notification";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { walletMarkMissing } from "./workspace.state.ts";

const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** How a logo's own pixels read on the wallet pass's dark ground. */
export type WalletMarkVerdict =
	| { kind: "derive"; reason: "dark single-tone" }
	| { kind: "offer"; reason: "dark coloured" | "dark two-tone" }
	| { kind: "keep"; reason: "reads on a dark ground" }
	| { kind: "unusable"; reason: "cannot be decoded" | "no visible ink" };

/**
 * What the picked file was found to be — every verdict the measurement can
 * return, plus the one answer it gives by throwing: a browser that cannot
 * supply a 2D context measured nothing, which is a reason not to save rather
 * than a verdict about the file.
 */
export type LogoAppraisal =
	| WalletMarkVerdict
	| {
			readonly kind: "unmeasurable";
			readonly reason: "the browser could not measure it";
	  };

/** A record rather than a `choose`, so a reason added to the union without
 *  copy fails the typecheck instead of rendering an empty line. */
const APPRAISAL_COPY = {
	"dark single-tone": "admin.settings.organization.walletMarkVerdictDerive",
	"dark coloured": "admin.settings.organization.walletMarkVerdictColor",
	"dark two-tone": "admin.settings.organization.walletMarkVerdictTwoTone",
	"reads on a dark ground": "admin.settings.organization.walletMarkVerdictKeep",
	"cannot be decoded":
		"admin.settings.organization.walletMarkVerdictUnreadable",
	"no visible ink": "admin.settings.organization.walletMarkVerdictBlank",
	"the browser could not measure it":
		"admin.settings.organization.walletMarkVerdictUnmeasurable",
} as const satisfies Record<LogoAppraisal["reason"], string>;

/** Whether a pending logo may be saved: only on a verdict that positively
 *  vouches for it. A measurement that has not answered is no verdict. */
export function logoMaySave(
	picked: boolean,
	appraisal: LogoAppraisal | null,
): boolean {
	return (
		!picked ||
		appraisal?.kind === "derive" ||
		appraisal?.kind === "offer" ||
		appraisal?.kind === "keep"
	);
}

export type OrganizationBrandFields = {
	readonly name: string;
	readonly org: string;
	readonly seed: string;
};

@schmancyElement("organization-brand")
export class OrganizationBrand extends LocalizeMixin(SchmancyElement) {
	private readonly logoInputRef = createRef<HTMLInputElement>();

	@property({ attribute: false }) value: OrganizationBrandFields = {
		name: "",
		org: "",
		seed: "",
	};
	/** What the two preview tiles render: the stored logo, a picked file's
	 *  object URL, or null. */
	@property({ attribute: false }) logoUrl: string | null = null;
	/** A pick or a removal is waiting to be saved. */
	@property({ type: Boolean, attribute: "logo-pending" }) logoPending = false;
	@property({ attribute: false }) logoAppraisal: LogoAppraisal | null = null;
	@property({ attribute: false }) walletMark: "derive" | "verbatim" =
		"verbatim";
	@property({ type: Boolean, attribute: "wallet-mark-requested" })
	walletMarkRequested = false;
	@property({ type: Boolean }) readonly = false;

	private readonly edit =
		(key: keyof OrganizationBrandFields) =>
		(e: CustomEvent<{ value: string }>): void => {
			// Held here before the host answers: autofill edits several fields
			// in one task, and each must build on the one before it.
			this.value = { ...this.value, [key]: e.detail.value };
			this.dispatchEvent(
				new CustomEvent<OrganizationBrandFields>("brand-change", {
					detail: this.value,
				}),
			);
		};

	private readonly pick = (e: Event): void => {
		const input = e.target;
		if (!(input instanceof HTMLInputElement)) return;
		const file = input.files?.[0] ?? null;
		input.value = "";
		if (!file) return;
		if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
			$notify.error(
				this.t("admin.settings.organization.logoUnsupportedTypeError"),
			);
			return;
		}
		// >= : storage.rules gates on request.resource.size < 2 MiB, strictly,
		// so a file at exactly the cap never passes here only to fail upload.
		if (file.size >= LOGO_MAX_BYTES) {
			$notify.error(this.t("admin.settings.organization.logoTooLargeError"));
			return;
		}
		this.dispatchEvent(new CustomEvent<File>("logo-pick", { detail: file }));
	};

	override render(): TemplateResult {
		return html`
			<div class="flex flex-col gap-4">
				<schmancy-input
					label=${this.t("admin.settings.organization.brandName")}
					required
					?readonly=${this.readonly}
					.errorMessages=${{
						valueMissing: this.t(
							"admin.settings.organization.requiredFieldMessage",
						),
					}}
					.value=${this.value.name}
					@input=${this.edit("name")}
				></schmancy-input>
				<schmancy-input
					label=${this.t("admin.settings.organization.brandOrg")}
					hint=${this.t("admin.settings.organization.brandOrgHint")}
					?readonly=${this.readonly}
					.value=${this.value.org}
					@input=${this.edit("org")}
				></schmancy-input>
				<schmancy-input
					type="color"
					label=${this.t("admin.settings.organization.brandSeed")}
					hint=${this.t("admin.settings.organization.brandSeedHint")}
					?readonly=${this.readonly}
					.value=${this.value.seed}
					@change=${this.edit("seed")}
				></schmancy-input>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.settings.organization.logo")}
					</schmancy-typography>
					<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
						${this.t("admin.settings.organization.logoHint")}
					</schmancy-typography>

					${when(
						this.logoUrl,
						(url) => html`
							<div class="grid grid-cols-2 gap-3">
								${repeat(
									["light", "dark"] as const,
									(scheme) => scheme,
									(scheme) => html`
										<schmancy-theme scheme=${scheme}>
											<schmancy-surface type="containerLow" class="flex items-center justify-center rounded-xl p-4">
												<div
													role="img"
													aria-label=${this.t(
														scheme === "light"
															? "admin.settings.organization.logoPreviewLightAriaLabel"
															: "admin.settings.organization.logoPreviewDarkAriaLabel",
													)}
													class="aspect-1500/326 w-full bg-surface-on"
													style="mask-image:url('${url}'); -webkit-mask-image:url('${url}'); mask-size:contain; -webkit-mask-size:contain; mask-repeat:no-repeat; -webkit-mask-repeat:no-repeat; mask-position:left center; -webkit-mask-position:left center;"
												></div>
											</schmancy-surface>
										</schmancy-theme>
									`,
								)}
							</div>
							<div class="flex gap-2">
								<schmancy-button variant="outlined" ?disabled=${this.readonly} @click=${() => this.logoInputRef.value?.click()}>
									${this.t("admin.settings.organization.logoChangeButton")}
								</schmancy-button>
								<schmancy-button
									variant="outlined"
									color="error"
									?disabled=${this.readonly}
									@click=${() => this.dispatchEvent(new CustomEvent("logo-remove"))}
								>
									${this.t("admin.settings.organization.logoRemoveButton")}
								</schmancy-button>
							</div>
						`,
						() => html`
							<schmancy-button
								variant="outlined"
								class="self-start"
								?disabled=${this.readonly}
								@click=${() => this.logoInputRef.value?.click()}
							>
								<schmancy-icon slot="prefix" aria-hidden="true">upload</schmancy-icon>
								${this.t("admin.settings.organization.logoChooseButton")}
							</schmancy-button>
						`,
					)}
					${when(
						this.logoAppraisal,
						(appraisal) => html`
							<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
								${this.t(APPRAISAL_COPY[appraisal.reason])}
							</schmancy-typography>
							${when(
								appraisal.kind === "offer",
								() => html`
									<schmancy-chips
										required
										.value=${this.walletMark}
										@change=${(e: CustomEvent<{ value: string }>) => {
											// programmatic echo of the value we already show — not a click
											if (e.detail.value === this.walletMark) return;
											if (
												e.detail.value !== "derive" &&
												e.detail.value !== "verbatim"
											)
												return;
											this.dispatchEvent(
												new CustomEvent<"derive" | "verbatim">(
													"wallet-mark-change",
													{
														detail: e.detail.value,
													},
												),
											);
										}}
									>
										<schmancy-filter-chip size="xs" .value=${"verbatim"}>
											${this.t("admin.settings.organization.walletMarkChoiceOriginal")}
										</schmancy-filter-chip>
										<schmancy-filter-chip size="xs" .value=${"derive"}>
											${this.t("admin.settings.organization.walletMarkChoiceLight")}
										</schmancy-filter-chip>
									</schmancy-chips>
								`,
							)}
						`,
					)}
					${when(walletMarkMissing.get() && !this.logoPending, () =>
						when(
							this.walletMarkRequested,
							() => html`
									<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
										${this.t("admin.settings.organization.walletMarkArmedNote")}
									</schmancy-typography>
								`,
							() => html`
									<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
										${this.t("admin.settings.organization.walletMarkMissingNote")}
									</schmancy-typography>
									<schmancy-button
										variant="outlined"
										class="self-start"
										?disabled=${this.readonly}
										@click=${() => this.dispatchEvent(new CustomEvent("wallet-mark-request"))}
									>
										${this.t("admin.settings.organization.walletMarkGenerateButton")}
									</schmancy-button>
								`,
						),
					)}
					<input
						${ref(this.logoInputRef)}
						type="file"
						accept="image/jpeg,image/png,image/webp"
						class="hidden"
						@change=${this.pick}
					/>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"organization-brand": OrganizationBrand;
	}
}
